import {hpcAttrs} from '@github-ui/hpc'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {CopilotAvatar} from '@github-ui/copilot-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import {MarkdownLastEditedBy} from '@github-ui/markdown-edit-history-viewer/MarkdownLastEditedBy'
import {IconButton, Label, Link, RelativeTime} from '@primer/react'
import {PencilIcon} from '@primer/octicons-react'
import {useRef, useState} from 'react'
import {clsx} from 'clsx'
import {ExpandableContent} from './ExpandableContent'
import {DescriptionEditor} from './DescriptionEditor'
import {useDescriptionData} from '../../page-data/loaders/use-description-data'
import {useOverviewRouteData} from './data/use-overview-route-data'
import type {SafeHTMLString} from '@github-ui/safe-html'
import styles from './Description.module.css'

import {useLayoutRouteData} from '../../page-data/loaders/use-layout-route-data'
import {useLayoutCanEditTitle} from '../../stores/layout-selectors'

/** PR description section on the Overview tab with expandable overflow */
export function Description() {
  const {
    description: initialDescriptionHtml,
    descriptionText: initialDescriptionText,
    editInformation,
  } = useOverviewRouteData()
  const {
    pullRequest: {author, createdTime},
  } = useLayoutRouteData()
  // Editing the title has the same permissions as editing the description
  const canEditDescription = useLayoutCanEditTitle()

  const {data: descriptionData} = useDescriptionData({
    initialBodyHtml: initialDescriptionHtml ?? undefined,
    initialBodyText: initialDescriptionText ?? undefined,
    initialEditInformation: editInformation,
  })
  // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string
  const description = descriptionData?.bodyHtml ? (descriptionData.bodyHtml as SafeHTMLString) : null
  const body = descriptionData?.body ?? ''

  const [isEditing, setIsEditing] = useState(false)
  const editButtonRef = useRef<HTMLButtonElement>(null)

  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const copilot = isCopilot(author.login)
  const appSlug = author.slug || author.login
  const profileUrl = copilot ? `/apps/${appSlug}` : `/${author.login}`
  const hovercardAttributes = copilot
    ? getHovercardAttributesForType('copilot', {botSlug: appSlug})
    : getHovercardAttributesForType('user', {login: author.login})
  const avatar = copilot ? (
    <CopilotAvatar size="medium" isDecorative className={styles.avatarImage} />
  ) : author.avatarUrl ? (
    <GitHubAvatar src={author.avatarUrl} size={24} className={styles.avatarImage} />
  ) : null

  return (
    <section className={styles.container} aria-label="Description" {...hpcAttrs()}>
      <div className={styles.header}>
        <div className={styles.author}>
          {avatar ? (
            <Link
              href={profileUrl}
              {...hovercardAttributes}
              aria-label={`${author.login}'s profile`}
              className={styles.avatar}
            >
              {avatar}
            </Link>
          ) : null}
          <Link href={profileUrl} {...hovercardAttributes} className={styles.authorName}>
            {author.login}
          </Link>
          {copilot && <Label variant="secondary">AI</Label>}
        </div>
        <span className={styles.opened}>opened {createdTime && <RelativeTime date={new Date(createdTime)} />}</span>
        <div className={styles.divider} aria-hidden="true" />
        <div className={styles.editMetadata}>
          <div className={styles.editHistory}>
            <MarkdownLastEditedBy editInformation={descriptionData?.editInformation} />
          </div>
          {canEditDescription && (
            <IconButton
              ref={editButtonRef}
              icon={PencilIcon}
              aria-label="Edit description"
              variant="invisible"
              size="small"
              className={clsx(styles.editButton, isEditing && 'v-hidden')}
              onClick={() => setIsEditing(true)}
            />
          )}
        </div>
      </div>
      <ExpandableContent
        sectionLabel="Description"
        storageKey="overview-description-expanded"
        defaultExpanded
        disabled={isEditing}
        contentClassName={styles.body}
      >
        <DescriptionEditor
          bodyHtml={description}
          body={body}
          isEditing={isEditing}
          onEditingChange={setIsEditing}
          returnFocusRef={editButtonRef}
        />
      </ExpandableContent>
    </section>
  )
}
